// Strategies API client — the read-only plan built from one Intelligence Report, keyed by the intel
// report id (docs: STRATEGY_API.md, Figma `Strategy - Playbook` 185-24983).
//
// Same thin promise wrappers as IntelligenceReport/intelligenceReportApi.js; `API_HOST` already
// supplies /api/v1, and arClient adds the Bearer token + tenant headers.
//
// GET  /intel-reports/{reportId}/strategy → data: StrategyState
// POST /intel-reports/{reportId}/strategy → HTTP 202 (body status 200), data: StrategyState with the
//      new run in `run` (status 'queued'). No body: the server decides whether it's the first run
//      (week 0, also "Try again" after a failed first run) or a refresh (the next week).
//      Errors: 404 (no such report), 403, 409 (a run is already queued / running), 422 (inputs not
//      finished, or the last week reached), 429 (5 starts a minute; `error` says how long to wait).
//
// StrategyState {
//   ready,             // every input report (audience, cultural, competitive) is finished
//   pendingInputs,     // ['audienceReport' | 'culturalReport' | 'competitiveReport'], [] when ready
//   run: { id, week, status: queued|running|completed|failed|cancelled, trigger: initial|refresh,
//          createdAt, completedAt|null, errorMessage|null } | null,   // the latest run
//   strategy: { … } | null                                             // the latest completed content
// }
//
// There is no list, progress, push channel, retry or delete endpoint: the page polls GET every 10 s
// while the run is queued / running (components/StrategiesLiveStatus.jsx). Every `imageUrl` in the
// content is a presigned S3 link valid for 60 minutes.

import arClient from '../../util/api_client';

/** '/intel-reports/42/strategy' */
const reportStrategyPath = (reportId) => `/intel-reports/${encodeURIComponent(reportId)}/strategy`;

/** GET — the report's latest run and its latest completed strategy. */
export const getReportStrategy = (reportId) =>
  new Promise((resolve) => arClient.doApiCallGet(reportStrategyPath(reportId), resolve));

/**
 * POST — start a run (first week, a retry after a failure, or the next week's refresh). Starting a
 * run isn't idempotent and is rate limited (5 a minute), so doApiCallV2's automatic retry on
 * 429 / 5xx / network errors is off: a retry would burn the limit or race the 409 guard.
 */
export const startReportStrategy = (reportId) =>
  new Promise((resolve) =>
    arClient.doApiCallV2(reportStrategyPath(reportId), {}, resolve, { retry: false })
  );
